import { Component, Input, OnInit, TemplateRef } from '@angular/core';
import { DataService } from 'src/app/services/data.service';

@Component({
  selector: 'app-tts-language-drawer',
  templateUrl: './tts-language-drawer.component.html',
  styleUrls: ['./tts-language-drawer.component.scss'],
  standalone: false
})
export class TtsLanguageDrawerComponent implements OnInit {
  @Input() labelContext = 'HomePage';
  @Input() tooltipLabel?: string;
  @Input() infoTooltip?: string;
  @Input() tooltipContent?: string;
  @Input() arrowXOffset?: number | string | null;
  @Input() arrowYOffset?: number | string | null;
  @Input() tooltipTemplate?: TemplateRef<any>; // HTML template
  @Input() tooltipArrowPosition: 'top' | 'bottom' | 'left' | 'right' | 'auto' = 'auto';
  @Input() tooltipArrowOffsetValue: 'auto' | number | string | any = 'auto';
  @Input() autoShow: boolean = true; // Automatically show on init if not seen
  @Input() showBackdrop: boolean = false; // Darken background
  @Input() highlightElement = true; // Highlight the target element
  @Input() tooltipUserId?: string | number; // Optional user ID - makes tooltips user-specific
  @Input() tooltipEnabled = true;
  @Input() compactMode = false; // For footer/navbar usage - hides chevron
  @Input() showChevron = true; // Control chevron visibility

  public ttsLanguagesDisplayed = false;
  public userttslanguages: any[] = [];
  public selectedTtsLanguageId: number | null = null;
  public isListOpen = false;
  public hasSelectedLanguage = false;
  public isTurnOffSelected = false;
  get currentLanguageId(): number {
    return Number(this.data?.user?.language_id ?? 0);
  }

  constructor(public data: DataService) {}

  ngOnInit() {
    this.refreshLanguageSelection();
  }

  /**
   * Refresh the selected TTS language based on current user profile
   * and available languages from DataService
   */
  private refreshLanguageSelection() {
    // Get fresh languages list from DataService
    this.userttslanguages = this.data.userttslanguages || [];
    
    // Get current language ID from user profile and convert to number
    // (API may return strings, but we need to compare as numbers)
    const currentLanguageId = this.currentLanguageId;
    
    // Check if "Turn Off" is selected (language_id = 0)
    this.isTurnOffSelected = currentLanguageId === 0;
    
    // Set selected language ID or null if Turn Off is selected
    if (this.isTurnOffSelected) {
      this.selectedTtsLanguageId = null;
    } else {
      // Find matching language in the list - convert language id to number for comparison
      // API may return language IDs as strings, so we need numeric comparison
      const matchingLanguage = this.userttslanguages.find(lang => Number(lang.id) === currentLanguageId);
      this.selectedTtsLanguageId = matchingLanguage ? currentLanguageId : null;
      
      // Debug: log if language wasn't found
      if (!matchingLanguage && currentLanguageId !== 0) {
        console.warn(`[TTS Drawer] Language ID ${currentLanguageId} not found in available languages:`, 
          this.userttslanguages.map(l => ({ id: l.id, name: l.name })));
      }
    }
    
    // Only consider a language selected if we have a valid match in the list
    this.hasSelectedLanguage = this.selectedTtsLanguageId !== null;
  }

  /**
   * 
   * @param input String representing 
   */
  labels(input: string) {
    return this.data.labels(input, this.labelContext);
  }

  get selectedTtsLanguage() {
    return this.userttslanguages.find(lang => lang.id === this.selectedTtsLanguageId);
  }

  get selectedTtsLanguageLabel() {
    if (this.isTurnOffSelected) {
      return this.labels('Turn off');
    }

    return this.selectedTtsLanguage ? this.selectedTtsLanguage.native_name : this.labels('Select a language');
  }

  get selectedTtsLanguageSubLabel() {
    if (this.isTurnOffSelected || this.selectedTtsLanguageId === null) {
      return '';
    }

    return this.selectedTtsLanguage ? this.selectedTtsLanguage.name : '';
  }

  openTtsDrawer() {
    // Refresh language selection from data service before opening drawer
    // console.log('[TTS Drawer] Opening drawer...');
    // console.log('[TTS Drawer] Current user language_id:', this.data?.user?.language_id);
    // console.log('[TTS Drawer] Available languages:', this.data.userttslanguages?.length, 'languages');
    
    this.refreshLanguageSelection();
    
    // console.log('[TTS Drawer] After refresh - selectedTtsLanguageId:', this.selectedTtsLanguageId);
    // console.log('[TTS Drawer] After refresh - isTurnOffSelected:', this.isTurnOffSelected);
    // console.log('[TTS Drawer] After refresh - hasSelectedLanguage:', this.hasSelectedLanguage);
    
    this.isListOpen = false;
    this.ttsLanguagesDisplayed = true;
  }

  closeTtsDrawer() {
    this.ttsLanguagesDisplayed = false;
  }

  onTtsDrawerDismiss() {
    this.ttsLanguagesDisplayed = false;
    this.isListOpen = false;
  }

  toggleList() {
    this.isListOpen = !this.isListOpen;
  }

  selectLanguage(languageId: number) {
    this.selectedTtsLanguageId = languageId;
    this.isTurnOffSelected = false;
    this.hasSelectedLanguage = true;
    this.isListOpen = false;
  }

  selectTurnOff() {
    this.selectedTtsLanguageId = null;
    this.isTurnOffSelected = true;
    this.hasSelectedLanguage = false;
    this.isListOpen = false;
  }

  saveTtsLanguagePreference() {
    // No language selected and nothing to turn off — nothing to save
    if (!this.isTurnOffSelected && this.selectedTtsLanguageId === null) {
      this.ttsLanguagesDisplayed = false;
      return;
    }

    const languageIdToSave = this.isTurnOffSelected ? 0 : this.selectedTtsLanguageId;

    // Selection unchanged — just close
    if (languageIdToSave === this.currentLanguageId) {
      this.ttsLanguagesDisplayed = false;
      return;
    }

    const event = this.isTurnOffSelected ? 'off' : 'on';
    this.selectTTSLanguage(languageIdToSave, event);
  }

  public selectTTSLanguage(language_id: number, event: string): void {
    if (this.data.ttsMediaPlayer != undefined) {
      this.data.ttsMediaPlayer.pause();
      this.data.ttsMediaPlayer.currentTime = 0;
      for (let i in this.data.ttsMediaPlayerSources) {
        this.data.ttsMediaPlayerSources[i] = '';
      }
    }

    if (language_id === 0) {
      this.data.setEventsAnalytics('/TTS_OFF', 'event');
    } else {
      const selectedLang = this.userttslanguages.find(lang => lang.id === language_id);
      if (selectedLang && selectedLang.name) {
        this.data.setEventsAnalytics(`/${selectedLang.name}_Selected`, 'event');
      }
    }

    this.data.updateUserTTSLanguage(language_id).then(() => {
      setTimeout(() => {
        this.ttsLanguagesDisplayed = false;
      }, 500);
    });
  }
}
